import React, { useMemo, useState } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page, Selection, Sort } from '@syncfusion/ej2-react-grids';
import { useAppSelector } from '../../../../../app/hooks';
import { ProcedureCodeEstimate } from '../../../../../services/ContractService/types/contractEstimateTypes';
import { AddEditEstProcedureDialog } from '../../dialogs/estimateDialogs';

const ProcedureCodeGrid: React.FC = () => {
  const estimate = useAppSelector(state => state.contractEstimate.currentEstimate);
  const isLoading = useAppSelector(state => state.contractEstimate.isLoading);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProcedure, setEditingProcedure] = useState<ProcedureCodeEstimate | null>(null);

  const procedureCodes = estimate?.procedureCodes || [];

  // Handle row selection for editing
  const handleRowSelection = (args: any) => {
    if (args.data) {
      const selectedProcedure = procedureCodes.find(pc => pc.id === args.data.id);
      if (selectedProcedure) {
        setEditingProcedure(selectedProcedure);
        setDialogOpen(true);
      }
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProcedure(null);
  };

  // Transform data for grid display
  const gridData = useMemo(() => {
    return procedureCodes;
  }, [procedureCodes]);

  // Template for currency fields
  const currencyTemplate = (field: string) => (props: any) => {
    const value = props[field] ?? 0;
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  // Template for units with decimal places
  const unitsTemplate = (props: any) => {
    const value = props.units ?? 0;
    return value.toFixed(2);
  };

  const gridSettings = {
    allowPaging: true,
    allowSorting: true,
    pageSettings: { 
      pageSize: 25,
      pageSizes: [10, 25, 50, 100] 
    },
    selectionSettings: { type: 'Single' as const, mode: 'Row' as const },
    rowHeight: 40,
    height: 300,
  };

  if (procedureCodes.length === 0) {
    return (
      <>
        <Alert severity="info">
          No procedure codes added yet. Click "Add Procedure Code" to create your first procedure code.
        </Alert>
        <AddEditEstProcedureDialog
          open={dialogOpen}
          editingProcedure={editingProcedure}
          onClose={handleCloseDialog}
        />
      </>
    );
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', mb: 1, fontSize: '0.75rem' }}>
        Click on any row to edit or delete a procedure code.
      </Typography>

      <Box sx={{ 
        border: '1px solid #e2e8f0',
        borderRadius: 1,
        overflow: 'hidden'
      }}>
        <GridComponent
          dataSource={gridData}
          {...gridSettings}
          width="100%"
          rowSelected={handleRowSelection}
          style={{
            border: 'none',
          }}
        >
          <ColumnsDirective>
            <ColumnDirective 
              field="priority" 
              headerText="Priority" 
              width="80"
              textAlign="Center"
              allowSorting={true}
            />
            <ColumnDirective 
              field="code" 
              headerText="Procedure Code" 
              width="120"
              textAlign="Left"
              allowSorting={true}
            />
            <ColumnDirective 
              field="units" 
              headerText="Units" 
              width="100"
              textAlign="Right"
              template={unitsTemplate}
              allowSorting={true}
            />
            <ColumnDirective 
              field="implantCost" 
              headerText="Implant Cost" 
              width="120"
              textAlign="Right"
              template={currencyTemplate('implantCost')}
              allowSorting={true}
            />
            <ColumnDirective 
              field="apcCode" 
              headerText="APC Override" 
              width="110"
              textAlign="Center"
              allowSorting={true}
            />
          </ColumnsDirective>
          <Inject services={[Page, Selection, Sort]} />
        </GridComponent>
      </Box>

      <AddEditEstProcedureDialog
        open={dialogOpen}
        editingProcedure={editingProcedure}
        onClose={handleCloseDialog}
      />
    </Box>
  );
};

export default ProcedureCodeGrid;